/**
 * @fileoverview Job task board (MOR-696 Stage 2)
 *
 * URL: /dashboard/jobs/:jobId/calendar/tasks
 * Protection: requires authentication + job access (via Convex queries).
 *
 * Sibling of the Key Dates calendar. Renders the job's tasks/milestones via the
 * shared `TaskSurface` (board <-> list, filters, grouping, saved views). This
 * route is a thin data-loading wrapper; all task UX lives in `TaskSurface` so
 * the per-job board and the workspace inbox never drift.
 *
 * Dependencies:
 *  - Convex: keyDates.queries.listForJob, jobs.queries.getJob,
 *    tasks.statuses.listByOrg, tasks.labels.listByOrg,
 *    organizations.queries.getOrganizationMembers
 *  - Components: TaskSurface
 */

import { useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";

import { TaskSurface, type BoardMember } from "@/calendar";
import { Spinner } from "@/ui/primitives/spinner";

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/jobs/$jobId/calendar/tasks",
)({
  // `task` deep-links straight to a task (by title) — used when jumping here
  // from a job automation (MOR-736).
  validateSearch: (search: Record<string, unknown>): { task?: string } => ({
    task: typeof search.task === "string" ? search.task : undefined,
  }),
  component: JobTasksPage,
});

function JobTasksPage() {
  const { jobId } = Route.useParams();
  const { task: openTaskTitle } = Route.useSearch();
  const typedJobId = jobId as Id<"jobs">;

  const { data: job } = useQuery(
    convexQuery(api.jobs.queries.getJob, { jobId: typedJobId }),
  );
  const orgId = job?.orgId;

  const { data: events } = useQuery(
    convexQuery(api.keyDates.queries.listForJob, { jobId: typedJobId }),
  );
  const { data: statuses } = useQuery({
    ...convexQuery(api.tasks.statuses.listByOrg, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });
  const { data: labels } = useQuery({
    ...convexQuery(api.tasks.labels.listByOrg, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });
  const { data: orgMembers } = useQuery({
    ...convexQuery(api.organizations.queries.getOrganizationMembers, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });

  const members = useMemo<BoardMember[]>(
    () =>
      (orgMembers ?? []).map((m) => ({
        _id: String(m.user._id),
        firstName: m.user.firstName,
        lastName: m.user.lastName,
        email: m.user.email,
        avatarUrl: m.user.avatarUrl,
      })),
    [orgMembers],
  );

  if (
    job === undefined ||
    events === undefined ||
    statuses === undefined ||
    !orgId
  ) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner className="text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-5 bg-muted p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Tasks</h1>
        <p className="text-sm text-muted-foreground">
          Plan and track the work for this job. Drag cards between columns to
          update their status. Click a card to edit its details.
        </p>
      </div>

      <TaskSurface
        scope="job"
        orgId={orgId}
        jobId={typedJobId}
        events={events}
        statuses={statuses}
        labels={labels ?? []}
        members={members}
        openTaskTitle={openTaskTitle}
      />
    </div>
  );
}
